<script setup lang="ts">
import BaseCard from "@/core/components/base/BaseCard.vue";
import {Healthcenter, HealthcenterOpening} from "@/core/interfaces/healthcenter/Healthcenter";
import {ref, computed} from "vue";
import Icon from "@/core/components/icon/Icon.vue";
import BaseInput from "@/core/components/base/BaseInput.vue";
import HealthcenterAddress from "@/healthcenter/components/HealthcenterAddress.vue";
import HealthcenterOpeningHours from "@/healthcenter/components/HealthcenterOpeningHours.vue";
import {healthcenterApi} from "@/container";

const {healthcenter} = defineProps<{ healthcenter: Healthcenter }>();
const emits = defineEmits(['update']);
const phoneRules = [
    value => (value && value.length <= 10) || 'Maximum 10 caractères',
    value => /[0-9]{10}/.test(value) || 'Numéro de téléphone non valide'
];

const updatedHealthcenter = ref<Healthcenter>({...healthcenter});

/**
 * Get contacts information.
 */
const mapContacts = (healthcenter: Healthcenter) => {
    const contact = healthcenter.contacts?.find(contact => contact.type?.slug === "booking");
    const facebook = healthcenter.contacts?.find(contact => contact.type?.slug === "facebook");
    const phone = healthcenter.contacts?.find(contact => contact.type?.slug === "phone");

    return {
        booking: contact?.value ?? "",
        facebook: facebook?.value ?? "",
        phone: phone?.value.replace(/\s/g, "") ?? ""
    };
};

const contacts = ref(mapContacts({...healthcenter}));

const isValid = computed(() => {
    for (let i = 0; i < phoneRules.length; i++) {
        let rule = phoneRules[i];
        if (rule(contacts.value.phone) !== true) {
            return false;
        }
    }
    return true;
});

/**
 * Check if healthcenter contact has been changed.
 */
const healthcenterContactChanged = (): boolean => {
    const contact = healthcenter.contacts?.find(contact => contact.type?.slug === "booking");
    const facebook = healthcenter.contacts?.find(contact => contact.type?.slug === "facebook");
    const phone = healthcenter.contacts?.find(contact => contact.type?.slug === "phone");

    return contacts.value.phone !== (phone?.value.replace(/\s/g, "") ?? "")
        || contacts.value.facebook !== (facebook?.value ?? "")
        || contacts.value.booking !== (contact?.value ?? "");
};

/**
 * Check if the MSSanté email has been changed.
 */
const mssanteEmailChanged = (): boolean => {
    return (healthcenter.mssante_email ?? '') !== (updatedHealthcenter.value.mssante_email ?? '');
};

/**
 * Update information.
 */
const updateInformation = () => {
    let formData: { name?: string; url?: string; mssante_email?: string | null; contacts?: any[] } = {};

    if (healthcenter.name !== updatedHealthcenter.value.name) {
        formData.name = updatedHealthcenter.value.name;
    }
    if (healthcenter.url !== updatedHealthcenter.value.url) {
        formData.url = updatedHealthcenter.value.url;
    }
    if (mssanteEmailChanged()) {
        formData.mssante_email = updatedHealthcenter.value.mssante_email || null;
    }
    if (healthcenterContactChanged()) {

        formData.contacts = [];

        if (contacts.value.booking) {
            formData.contacts.push({slug: 'booking', value: contacts.value.booking});
        }

        if (contacts.value.facebook) {
            formData.contacts.push({slug: 'facebook', value: contacts.value.facebook});
        }

        if (contacts.value.phone && isValid.value) {
            formData.contacts.push({slug: 'phone', value: contacts.value.phone});
        }
    }

    emits('update', formData);
};

/**
 * Show the hours the dialog just saved, reading the pharmacy again when the
 * dialog could not read the sheet back.
 */
const onOpeningSaved = (opening: HealthcenterOpening | null) => {
    if (opening) {
        updatedHealthcenter.value = {...updatedHealthcenter.value, openings: [opening]};

        return;
    }

    healthcenterApi().read(updatedHealthcenter.value.id)
        .then(response => updatedHealthcenter.value = {...updatedHealthcenter.value, openings: response.openings})
        .catch(() => undefined);
};
</script>

<template>
  <BaseCard class="relative p-4 pb-16">
    <h2>Ma pharmacie</h2>
    <div class="grid grid-cols-2 gap-10 py-5">
      <BaseInput
        label="Nom"
        :value="updatedHealthcenter.name"
        type="text"
        class="rounded-lg"
        @input="value => updatedHealthcenter.name = value"
      />
      <HealthcenterOpeningHours :healthcenter="updatedHealthcenter" @saved="onOpeningSaved" />
      <HealthcenterAddress
        :healthcenter="updatedHealthcenter"
        @update-address="value => updatedHealthcenter.address = value"
      />
      <BaseInput
        label="Rendez-vous"
        :value="contacts.booking"
        type="url"
        placeholder="Doctolib, Maiia, ..."
        class="rounded-lg"
        @input="value => contacts.booking = value"
      />
      <BaseInput
        label="Numéro de téléphone"
        :value="contacts.phone"
        type="tel"
        :errors="isValid ? [] : ['Numéro de téléphone non valide']"
        class="rounded-lg"
        @input="value => contacts.phone = value"
      />
      <BaseInput
        label="Facebook"
        :value="contacts.facebook"
        type="url"
        placeholder="ex. : https://facebook.com/ma-pharmacie"
        class="rounded-lg"
        @input="value => contacts.facebook = value"
      />
      <BaseInput
        label="Site internet"
        :value="updatedHealthcenter.url ?? ''"
        type="url"
        placeholder="https://monsite.fr"
        class="rounded-lg"
        @input="value => updatedHealthcenter.url = value"
      />
      <div>
        <label class="flex items-center gap-1.5 text-sm font-medium text-gray-700">
          <img src="@/assets/images/logos/logo-ms-sante.png" class="h-4 rounded-full" alt="MSSante">
          Adresse MSSanté de la pharmacie
        </label>
        <BaseInput
          :value="updatedHealthcenter.mssante_email ?? ''"
          type="email"
          placeholder="ex : mail@pharmacien.mssante.fr"
          class="rounded-lg"
          @input="value => updatedHealthcenter.mssante_email = value"
        />
      </div>
    </div>
    <div
      v-if="(healthcenterContactChanged() || mssanteEmailChanged() || healthcenter.name !== updatedHealthcenter.name || healthcenter.url !== updatedHealthcenter.url)"
      class="absolute bottom-5 right-5 flex flex-1 justify-center p-4 rounded-full cursor-pointer bg-primary gap-2"
      @click="updateInformation"
    >
      <Icon name="mdi-content-save" color="white" />
      <p class="font-semibold">
        Enregistrer
      </p>
    </div>
  </BaseCard>
</template>
